import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Globe, TrendingUp, TrendingDown, Zap } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { countriesAPI } from '../services/api';

const Countries = () => {
  const [countries, setCountries] = useState([]);
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    const filtered = countries.filter(country =>
      country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      country.code.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredCountries(filtered);
  }, [searchTerm, countries]);

  const fetchCountries = async () => {
    try {
      const response = await countriesAPI.getAll();
      setCountries(response.data);
      setFilteredCountries(response.data);
    } catch (error) {
      console.error('Error fetching countries:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEOHead
        title="Countries Trade Data & Global Trade Index Scores"
        description="Explore trade data, tariff rates, import export policies, sanctions, and Global Trade Index (GTI) scores for 190+ countries. Compare country trade profiles and track trade events."
        path="/countries"
        keywords="country trade data, global trade index, GTI scores, import export by country, tariff rates by country, trade policy, country trade profile"
      />
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-on-surface">Countries</h1>
        <p className="mt-2 text-slate-600 dark:text-on-surface-variant">Explore trade data by country</p>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400 dark:text-outline" />
          <input
            type="text"
            placeholder="Search countries..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-stitch-lg border border-slate-300 bg-white py-3 pl-10 pr-4 text-slate-900 placeholder-slate-400 focus:border-transparent focus:ring-2 focus:ring-primary-500 dark:border-outline-variant/25 dark:bg-surface-container-high dark:text-on-surface dark:placeholder-slate-500"
          />
        </div>
      </div>

      {/* Countries Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredCountries.map((country) => (
          <Link
            key={country.id}
            to={`/countries/${country.id}`}
            className="rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-6 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <Globe className="h-8 w-8 text-primary-600 dark:text-primary mr-3" />
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-on-surface">{country.name}</h3>
                  <p className="text-sm text-slate-500 dark:text-on-surface-variant">{country.code}</p>
                </div>
              </div>
              {country.gti_score != null && (
                <div className="text-right">
                  <p className={`text-xl font-bold ${country.gti_score >= 50 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {country.gti_score.toFixed(1)}
                  </p>
                  <p className="text-xs text-slate-400 dark:text-outline">GTI</p>
                </div>
              )}
            </div>
            <div className="space-y-1">
              {country.region && (
                <p className="text-sm text-slate-600 dark:text-on-surface-variant">
                  <span className="font-medium">Region:</span> {country.region}
                </p>
              )}
              {country.gti_rank != null && (
                <p className="text-sm text-slate-600 dark:text-on-surface-variant">
                  <span className="font-medium">Rank:</span> #{country.gti_rank}
                  {country.trend && (
                    <span className="ml-2 inline-flex items-center">
                      {country.trend === 'improving' ? (
                        <TrendingUp className="h-3 w-3 text-green-500 dark:text-green-400 inline" />
                      ) : country.trend === 'declining' ? (
                        <TrendingDown className="h-3 w-3 text-red-500 dark:text-red-400 inline" />
                      ) : null}
                    </span>
                  )}
                </p>
              )}
              {country.event_count > 0 && (
                <p className="text-sm text-slate-600 dark:text-on-surface-variant flex items-center">
                  <Zap className="h-3 w-3 text-yellow-500 mr-1" />
                  {country.event_count} trade events
                </p>
              )}
            </div>
          </Link>
        ))}
      </div>

      {filteredCountries.length === 0 && (
        <div className="text-center py-12">
          <p className="text-slate-500 dark:text-on-surface-variant">No countries found matching your search.</p>
        </div>
      )}
    </div>
  );
};

export default Countries;
